import React, { useEffect, useState, useRef, useCallback } from "react";
import AdminLayout from "../../UI/Layout/AdminLayout";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Axios from "../../../axios/Axios";
import { NODE_HOST_URL_v1 } from "../../Dashboard/api/Api";
import { userloginDetails } from "../../Utils/AdminAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch, faTimes, faChevronLeft, faChevronRight,
  faSpinner, faMobileAlt, faClock, faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import Title from "../../UI/Text/Title";

const TooltipCell = ({ text, placement = "top" }) => {
  const [show, setShow] = useState(false);
  if (!text || text === "—") return <span style={{ fontSize: "12px", color: "#475569" }}>—</span>;

  const isBottom = placement === "bottom";
  const tooltipPos = isBottom
    ? { top: "calc(100% + 8px)" }
    : { bottom: "calc(100% + 8px)" };
  const arrowPos = isBottom
    ? { bottom: "100%", borderBottomColor: "#1e293b", borderTopColor: "transparent" }
    : { top: "100%", borderTopColor: "#1e293b", borderBottomColor: "transparent" };

  return (
    <div
      style={{ position: "relative", display: "inline-block", maxWidth: "160px" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span style={{
        fontSize: "12px", color: "#475569",
        display: "-webkit-box", WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
        cursor: "default",
      }}>
        {text}
      </span>
      {show && (
        <div style={{
          position: "absolute",
          ...tooltipPos,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#1e293b",
          color: "#f8fafc",
          padding: "8px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          lineHeight: "1.6",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxWidth: "300px",
          minWidth: "160px",
          zIndex: 9999,
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          pointerEvents: "none",
        }}>
          {text}
          <div style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            border: "6px solid transparent",
            ...arrowPos,
          }} />
        </div>
      )}
    </div>
  );
};

const STATUS_CONFIG = {
  delivered:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  failed:     { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  sent:       { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  read:       { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  delivered_to_network: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  default:    { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
};

const getStatusStyle = (status) =>
  STATUS_CONFIG[status ? status.toLowerCase() : ""] || STATUS_CONFIG.default;

const RcsAdminDetailReport = () => {
  const lStorage   = userloginDetails();
  const userId     = lStorage && lStorage.user ? lStorage.user.userId : null;
  const userToken  = lStorage ? lStorage.token : null;

  const { id, campaignName, contactcount, fromdate, todate, username, msg_count } = useParams();
  const msgCount = parseInt(msg_count, 10) || 1;
  const totalDataCount = Math.ceil((parseInt(contactcount, 10) || 0) / msgCount);
  const perPageRef = useRef(10);

  const [loading,       setLoading]       = useState(false);
  const [detailReport,  setDetailReport]  = useState([]);
  const [error,         setError]         = useState(null);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalRecords,  setTotalRecords]  = useState(totalDataCount);
  const [perPage,       setPerPage]       = useState(10);
  const [searchInput,   setSearchInput]   = useState("");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [windowWidth,   setWindowWidth]   = useState(window.innerWidth);

  const fetchReport = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await Axios.post(`${NODE_HOST_URL_v1}/AdminRcs`, {
        token:       userToken,
        user_id:     userId,
        campaign_id: id,
        method:      "view_rcs_camp",
        fromdate:    fromdate,
        todate:      todate,
        page:        page,
      });

      if (data && data.data && data.data[0]) {
        const records  = Array.isArray(data.data[0].Report) ? data.data[0].Report : [];
        const pagInfo  = data.data[0].pagination || {};
        const apiPages = parseInt(pagInfo.totalPages, 10) || 1;

        if (page === 1 && records.length > 0) {
          perPageRef.current = records.length;
          setPerPage(records.length);
        }
        const pp    = perPageRef.current;
        const pages = totalDataCount > 0 && pp > 0
          ? Math.ceil(totalDataCount / pp)
          : apiPages;

        setDetailReport(records);
        setTotalRecords(totalDataCount > 0 ? totalDataCount : (pagInfo.totalRecords || 0));
        setTotalPages(Math.max(1, pages));
        setCurrentPage(page);
      } else {
        setError((data && data.message) || "Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, id, fromdate, todate, totalDataCount]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  useEffect(() => {
    fetchReport(1);
  }, [fetchReport]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchReport(page);
  };

  const renderPagePills = () => {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, []);

    return pages.map((p, idx) =>
      p === "..." ? (
        <span key={"e" + idx} style={{ padding: "0 6px", color: "#94a3b8", fontSize: "13px" }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          style={{
            minWidth: "34px", height: "34px",
            border: "1px solid",
            borderColor: currentPage === p ? "#4f46e5" : "#e2e8f0",
            background:  currentPage === p ? "#4f46e5" : "#fff",
            color:       currentPage === p ? "#fff" : "#475569",
            borderRadius: "8px", fontSize: "13px",
            fontWeight: currentPage === p ? "700" : "400",
            cursor: "pointer",
          }}
        >
          {p}
        </button>
      )
    );
  };

  const filteredRows = searchQuery
    ? detailReport.filter(r => {
        const q = searchQuery.toLowerCase();
        return (r.phone_number && r.phone_number.toLowerCase().includes(q)) ||
               (r.status && r.status.toLowerCase().includes(q));
      })
    : detailReport;

  const COLUMNS = ["Phone Number", "Country", "Campaign Message", "Sent Time", "Delivery Time", "Count", "Status", "Error Code", "Reason"];

    const filterdata = async () => {
      if (!searchInput.trim()) return;
      setLoading(true);
      setError(null);
      try {
        const payload = {
          user_id: userId,
          method: "NumberSearch",
          number: searchInput,
          campaign_id: id,
          username: username,
          filter : "rcsSearch",
          token: userToken,
        };
        const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/CampaignSearch`, payload);
        if (data.success === true) {
          setDetailReport(Array.isArray(data.data) ? data.data : []);
          setSearchQuery(searchInput.trim());
        } else {
          setDetailReport([]);
          setSearchQuery(searchInput.trim());
          setError(data.message || "No results found.");
        }
      } catch (err) {
        setError(err.response.data.message || "Network error during search.");
      } finally {
        setLoading(false);
      }
    };

  return (
    <AdminLayout>
      <main className="content">
        <div className="container-fluid p-0">
          <Title isButton={false}>RCS Campaign Report</Title>

          {error && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "12px",
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "12px", padding: "14px 18px", marginBottom: "16px",
            }}>
              <span style={{ fontSize: "18px", lineHeight: 1 }}>&#9888;</span>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#dc2626", marginBottom: "2px" }}>Something went wrong</div>
                <div style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div>
              </div>
              <button
                onClick={() => setError(null)}
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#f87171", fontSize: "16px", lineHeight: 1, padding: 0 }}
              >
                &#x2715;
              </button>
            </div>
          )}

          <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            fontFamily: "Inter, system-ui, sans-serif",
            overflow: "hidden",
          }}>
            {/* ── Header ── */}
            <div style={{
              padding: "20px 24px", borderBottom: "1px solid #f1f5f9",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: "12px",
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: isMobile ? "15px" : "18px", fontWeight: "800", color: "#0f172a" }}>
                  Campaign ID: <span style={{ color: "#4f46e5" }}>{id}</span>
                </h3>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "8px", width: isMobile ? "100%" : "auto" }}>
                <div style={{ position: "relative", flex: isMobile ? 1 : "none" }}>
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px", pointerEvents: "none" }}
                  />
                  <input
                    type="text"
                    placeholder="Search phone number..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") filterdata(); }}
                    style={{
                      paddingLeft: "30px", paddingRight: searchInput ? "30px" : "12px",
                      height: "36px", border: "1px solid #e2e8f0", borderRadius: "8px",
                      fontSize: "13px", color: "#334155", outline: "none",
                      width: isMobile ? "100%" : "220px", background: "#f8fafc",
                      boxSizing: "border-box",
                    }}
                  />
                  {searchInput && (
                    <button
                      onClick={() => { setSearchInput(""); setSearchQuery(""); setError(null); fetchReport(1); }}
                      style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94a3b8", fontSize: "11px" }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
                <button
                  onClick={filterdata}
                  style={{
                    height: "36px",
                    padding: isMobile ? "0 12px" : "0 16px",
                    background: "#4f46e5", color: "#fff",
                    border: "none", borderRadius: "8px",
                    fontSize: "13px", fontWeight: "600", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} style={{ fontSize: "11px" }} />
                  {!isMobile && " Search"}
                </button>
              </div>
            </div>

            {/* ── Table ── */}
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0", flexDirection: "column", gap: "12px" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "28px", color: "#4f46e5" }} />
                <span style={{ color: "#64748b", fontSize: "13px" }}>Loading records…</span>
              </div>
            ) : filteredRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
                <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: "14px" }}>No records found.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1050px" }}>
                  <thead style={{ background: "#f8fafc" }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      {COLUMNS.map((h, i) => (
                        <th key={i} style={{
                          padding: "12px 16px", fontSize: "11px", fontWeight: "700",
                          color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
                          textAlign: "left", whiteSpace: "nowrap",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((x, i) => {
                      const sts = getStatusStyle(x.status);
                      return (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          {/* Phone Number */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{x.phone_number || "—"}</span>
                            </div>
                          </td>

                          {/* Country */}
                          <td style={{ padding: "14px 16px", fontSize: "12px", color: "#475569" }}>
                            {x.country_code || "—"}
                          </td>

                          {/* Campaign Message */}
                          <td style={{ padding: "14px 16px", maxWidth: "200px" }}>
                            <span style={{
                              fontSize: "12px", color: "#475569",
                              display: "-webkit-box", WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
                            }}>
                              {x.parameters || "—"}
                            </span>
                          </td>

                          {/* Sent Time */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
                              <span style={{ fontSize: "12px", color: "#475569" }}>{x.sent_time || "—"}</span>
                            </div>
                          </td>

                          {/* Delivery Time */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <FontAwesomeIcon
                                icon={x.deliver_time ? faCheckCircle : faClock}
                                style={{ color: x.deliver_time ? "#16a34a" : "#cbd5e1", fontSize: "11px" }}
                              />
                              <span style={{ fontSize: "12px", color: "#475569" }}>{x.deliver_time || "—"}</span>
                            </div>
                          </td>

                          {/* Count */}
                          <td style={{ padding: "14px 16px", fontSize: "12px", color: "#475569", textAlign: "center" }}>
                            {x.msg_count || "—"}
                          </td>

                          {/* Status */}
                          <td style={{ padding: "14px 16px" }}>
                            <span style={{
                              background: sts.bg, color: sts.color,
                              border: "1px solid " + sts.border,
                              padding: "3px 10px", borderRadius: "100px",
                              fontSize: "11px", fontWeight: "700",
                              textTransform: "capitalize", whiteSpace: "nowrap",
                            }}>
                              {x.status || "—"}
                            </span>
                          </td>

                          {/* Error Code */}
                          <td style={{ padding: "14px 16px", maxWidth: "160px" }}>
                            <span style={{
                              fontSize: "12px", color: "#475569",
                              display: "-webkit-box", WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
                            }}>
                              {x.api_status || "—"}
                            </span>
                          </td>

                          {/* Reason */}
                          <td style={{ padding: "14px 16px", maxWidth: "160px" }}>
                            <TooltipCell text={x.api_response || "—"} placement={i === 0 ? "bottom" : "top"} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Pagination ── */}
            {!loading && detailReport.length > 0 && !searchQuery && (
              <div style={{
                padding: "16px 24px", borderTop: "1px solid #f1f5f9",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: "10px",
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {totalRecords > 0 ? (
                    <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>
                      Showing{" "}
                      <span style={{ color: "#4f46e5" }}>{(currentPage - 1) * perPage + 1}</span>
                      {" – "}
                      <span style={{ color: "#4f46e5" }}>{Math.min(currentPage * perPage, totalRecords)}</span>
                      {" of "}
                      <span style={{ color: "#4f46e5" }}>{totalRecords}</span>
                      {" records"}
                    </span>
                  ) : (
                    <span style={{ fontSize: "13px", color: "#64748b" }}>
                      Page <b style={{ color: "#4f46e5" }}>{currentPage}</b> of <b style={{ color: "#4f46e5" }}>{totalPages}</b>
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: isMobile ? "space-between" : "flex-end" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      height: "34px", padding: "0 14px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage === 1 ? "#f8fafc" : "#fff",
                      color:      currentPage === 1 ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "10px" }} /> Prev
                  </button>

                  {isMobile ? (
                    <span style={{ fontSize: "13px", color: "#475569", fontWeight: "600" }}>
                      {currentPage} / {totalPages}
                    </span>
                  ) : renderPagePills()}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                      height: "34px", padding: "0 14px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage >= totalPages ? "#f8fafc" : "#fff",
                      color:      currentPage >= totalPages ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "10px" }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </AdminLayout>
  );
};

export default RcsAdminDetailReport;
